import type { DirectoryRow } from "~/systems/model/directory-row";
import { isInaccessibleResourceRow, isUnresolvedAccessResourceRow } from "~/systems/model/directory-row";
import { getFileNameAndExtension, normalizeFileExtension } from "~/utils/file";

type ResourceDirectoryRow = Extract<DirectoryRow, { kind: "resource" }>;

const README_BASENAME = "readme";
const README_EXTENSIONS = new Set(["md", "html"]);

export function isReadmeResourceRow(row: DirectoryRow): row is ResourceDirectoryRow {
  if (row.kind !== "resource") return false;
  const extension = readmeExtension(row);
  return readmeBasename(row) === README_BASENAME && extension !== undefined && README_EXTENSIONS.has(extension);
}

export function isDisplayableReadmeResourceRow(row: ResourceDirectoryRow): boolean {
  return !isInaccessibleResourceRow(row) && !isUnresolvedAccessResourceRow(row) && Boolean(row.resourceId);
}

export function pickRootReadmeForDisplay(rows: readonly DirectoryRow[]): ResourceDirectoryRow | null {
  return pickPreferredReadmeRow(
    rows.filter(
      (row): row is ResourceDirectoryRow =>
        row.depth === 0 && isReadmeResourceRow(row) && isDisplayableReadmeResourceRow(row),
    ),
  );
}

export function isReadmeListedRevision(revision: {
  readonly displayName?: string | null;
  readonly name?: string | null;
  readonly extension?: string | null;
}): boolean {
  const label = revision.displayName || revision.name || "";
  const parsed = getFileNameAndExtension(label);
  const basename = parsed?.name?.toLowerCase();
  const extension =
    normalizeFileExtension(parsed?.extension) ?? normalizeFileExtension(revision.extension ?? undefined);
  return basename === README_BASENAME && extension !== undefined && README_EXTENSIONS.has(extension);
}

export function pickPreferredReadmeRow(candidates: readonly DirectoryRow[]): ResourceDirectoryRow | null {
  let preferred: ResourceDirectoryRow | null = null;

  for (const row of candidates) {
    if (!isReadmeResourceRow(row)) continue;
    if (preferred === null || compareReadmeRows(row, preferred) < 0) {
      preferred = row;
    }
  }

  return preferred;
}

export function readmeResourceExtension(row: ResourceDirectoryRow): string {
  return readmeExtension(row) ?? "";
}

export function compareReadmeRows(left: ResourceDirectoryRow, right: ResourceDirectoryRow): number {
  const extensionOrder = readmeExtensionRank(left) - readmeExtensionRank(right);
  if (extensionOrder !== 0) return extensionOrder;

  const leftUpdatedAt = left.updatedAt?.getTime() ?? Number.NEGATIVE_INFINITY;
  const rightUpdatedAt = right.updatedAt?.getTime() ?? Number.NEGATIVE_INFINITY;
  if (leftUpdatedAt !== rightUpdatedAt) return rightUpdatedAt - leftUpdatedAt;

  return left.name.localeCompare(right.name);
}

function readmeExtensionRank(row: ResourceDirectoryRow): number {
  const extension = readmeExtension(row);
  if (extension === "html") return 0;
  if (extension === "md") return 1;
  return 2;
}

function readmeBasename(row: ResourceDirectoryRow): string | undefined {
  const parsed = getFileNameAndExtension(row.name);
  const name = parsed?.name ?? row.name;
  return name ? name.toLowerCase() : undefined;
}

function readmeExtension(row: ResourceDirectoryRow): string | undefined {
  const parsed = getFileNameAndExtension(row.name);
  const fromName = normalizeFileExtension(parsed?.extension);
  if (fromName) return fromName;
  return normalizeFileExtension(row.extension);
}
